import { Injectable, ElementRef } from "@angular/core";
import { Subject, of } from 'rxjs';
import { concatMap, delay, tap } from 'rxjs/operators';
import { HttpClient } from "@angular/common/http";
import { UserInfoService, AemService } from "abs-ngx-library/src/lib/core";

// Window global declaration
function _window(): any {
  return window;
}

@Injectable({
  providedIn: "root",
})
export class BannerUnitService {

  public enableNgAllDealsMediumBanners: boolean = true;
  private bannerIndexMap = new Map<number, string>();
  private callbackQueue = new Subject<() => void>();
  public bannerQueueArray = [];
  private deviceSize: 'sm' | 'md' | 'lg' = 'lg';
  public bannerElementsHtmlMap = new Map<string, any>();

  constructor(private _http: HttpClient, private _userInfoService: UserInfoService, private absAemService: AemService) {
    // Initialize the Execution Queue for Banners
    this.callbackQueue.pipe(
      // concatMap ensures sequential execution
      concatMap(callback => {
        // Return an observable that first delays, then executes the callback
        return of(null).pipe(
          delay(1200), // Delay the emission by 1 second
          tap(() => { // Use tap to perform a side effect (execute the callback)
            callback(); // Execute the callback here, after the delay
          })
        );
      })
    ).subscribe();
    console.warn("Preparing the service");
    const dataElement = document.getElementById('gamBanners');
    if(dataElement) {
        let html = dataElement.getAttribute('data-aem-config');
        if (html) {
            html = this.decodeBase64UTF8(html);
            // Read the HTML Data Attribute
            const parser = new DOMParser(); // Create a new DOMParser instance
            const doc = parser.parseFromString(html, 'text/html');
            const carousel = doc.querySelector('.abs-carousel-proxy');
            const myListNextGenBannerUnit = doc.querySelector('.nextgen-banner-unit_MediumBanner');
            if (myListNextGenBannerUnit) {
              this.bannerElementsHtmlMap.set('.nextgen-banner-unit_MediumBanner', myListNextGenBannerUnit?.parentElement);
            }
            if (carousel) { // Check if the carousel exists
                //check the GAM feature flag to turn off the banners
                if (this.absAemService.getFeatureFlagByNameDisable('allDealsGAMBannersDisabled')) {
                    const gamBanners = carousel.querySelectorAll('.google-adManager');
                    if (gamBanners && gamBanners.length > 0) {
                        //loop on the GAM Banners
                        for (let i = 0; i < gamBanners.length; i++) {
                            gamBanners[i]?.closest('.banner-unit-proxy')?.remove();
                        }
                    }
                }
                this.bannerElementsHtmlMap.set('.abs-carousel', carousel);
                carousel.remove();
            }
            const banners = doc.querySelectorAll('[data-bannerposition]'); // Select all banners
            banners.forEach((banner: any) => {
                if(banner) {
                    banner.setAttribute('data-spapage', 'true');
                    this.bannerElementsHtmlMap.set(banner.getAttribute('data-bannerposition'), banner?.parentElement?.parentElement);
                }
            });
        }
    } else {
       //This is for AEM specific scenario where gamBanners do not exists
       //Check for All Deals page and if banners are enabled or not
       if (window?.location?.href?.includes('foru/coupons-deals')) {
           //Get banners on AEM All Deals page
           const banners = document?.querySelectorAll('[data-bannerposition="midb1"], [data-bannerposition="midb2"], [data-bannerposition="midb3"]');          
            banners?.forEach((banner: any) => {
                const bannerElement = banner?.parentElement?.parentElement;
                if(bannerElement) {
                    bannerElement.querySelector('[data-lazy-function]').innerHTML = '';
                    this.bannerElementsHtmlMap.set(banner.getAttribute('data-bannerposition'), bannerElement.cloneNode(true));
                    bannerElement.remove();
                }
            });
       }
    }
  }

  private decodeBase64UTF8(str: string): string {
  try {
    // Method 1: Using decodeURIComponent and escape
    return decodeURIComponent(escape(atob(str)));
  } catch (e) {
    // Fallback method using TextDecoder
    try {
      return new TextDecoder('utf-8').decode(
        Uint8Array.from(atob(str), c => c.charCodeAt(0))
      );
    } catch (fallbackError) {
      return atob(str); // Return basic decoding as last resort
    }
  }
}

  public addToQueue(callback: any): void {
    if(this.bannerQueueArray.indexOf(callback.bannerposition) == -1) {
        this.bannerQueueArray.push(callback.bannerposition);
        this.callbackQueue.next(callback.executeBanner.bind(callback)); // Push to the private Subject
    }
  }

  private deviceRowCoupons: { [key: string]: number } = {
    sm: 1,  // Mobile: has 1 card in row
    md: 2,  // Tablet: has 2 cards in row
    lg: 3   // Desktop: has 3 cards in row
  };
  shouldDisplayBanner(
    currentIndex: number
  ): boolean {
    return this.bannerIndexMap.has(currentIndex);
  }

  public getBannerElementsHtmlMap(): Map<string, any> {
    return this.bannerElementsHtmlMap;
  }

  getBannerIndexMap(): Map<number, string> {
    return this.bannerIndexMap;
  }

  // This method is used to prepare the banner index map from the coupons
  public prepareBannerIndexMap(banners, coupons): string[] {
    //clear existing map
    this.bannerIndexMap.clear();
    //Set the device size
    const width = window.innerWidth;
    this.deviceSize = width < 768 ? 'sm' : width < 992 ? 'md' : 'lg';
    console.warn('test-prepareBannerIndexMap: ' + this.deviceSize);
    //Number of coupons in a row
    const rowCoupons = this.deviceRowCoupons[this.deviceSize];
    //Row index after which we need to show the banner. At the moment this is hardcoded for SPA
    const rowNum = 3;

    // Iterate through the coupons and set the banner index map
    for (let i = 0, couponIndex = 1; i < coupons.length; i++, couponIndex++) {
      // Get the mod result
      const modResult = (couponIndex - 1) % rowCoupons;
      // If the coupon is a group split, we need to adjust the coupon index by adding the remaining coupons
      if (i !== 0 && coupons[i].isGroupSplit && modResult !== 0) {
        // If the coupon is a group split, we need to adjust the coupon index by adding the remaining coupons
        couponIndex += (rowCoupons - modResult);
      }

      //Calculate the number of coupons based on number of cou
      if (couponIndex % (rowCoupons * rowNum) !== 0) {
        continue;
      }
      const banner = banners.shift();
      if (!banner) {
        break;
      }
      // Set the banner index map
      this.bannerIndexMap.set(i, banner);
    }
    return banners;
  }

    // Function to track if the Ad slot is loaded
    public trackSlotLoaded(slotId: string, bannerElem: any, bannerUnitRef: ElementRef) {
        const window = _window();

        // Fallback if the slot is not filled within the timeout
        setTimeout(() => {
            if(window['googletag']?.pubads && window['googletag']?.pubads()?.getSlots) {
                window['googletag']?.pubads()?.getSlots()?.forEach((slot: any) => {
                    if (slot?.getSlotElementId()?.includes(slotId) && !slot?.getResponseInformation()) {
                        bannerElem.classList.add('collapse'); // Hide the banner
                        bannerUnitRef?.nativeElement?.parentElement?.classList?.add('d-none');
                    }
                });
            }
            if(!window['googletag'] || !bannerUnitRef?.nativeElement?.innerHTML) {
               bannerUnitRef?.nativeElement?.parentElement?.classList?.add('d-none');
            }
        }, 8000);
    }

}
